import React from 'react';
import { Button } from '../ui/Button';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, 
  FileText, 
  BarChart3, 
  Plus,
  Sparkles,
  Target
} from 'lucide-react';

interface QuickActionsProps {
  selectedProject?: any;
}

export function QuickActions({ selectedProject }: QuickActionsProps) {
  const navigate = useNavigate();

  const quickActions = [
    {
      title: 'Ask Analytics Agent',
      description: selectedProject ? 'Get insights from your GA4 data' : 'Select a project first',
      icon: MessageSquare,
      color: 'from-blue-600 to-blue-700',
      href: selectedProject ? '/agents/data' : '/projects',
      disabled: !selectedProject
    },
    {
      title: 'Create Report',
      description: selectedProject ? 'Build a custom marketing report' : 'Select a project first',
      icon: FileText,
      color: 'from-purple-600 to-purple-700',
      href: selectedProject ? '/reports' : '/projects',
      disabled: !selectedProject,
      beta: true
    },
    {
      title: 'View Analytics',
      description: 'Coming Soon',
      icon: BarChart3,
      color: 'from-green-600 to-green-700',
      href: '/analytics',
      disabled: true,
      comingSoon: true
    },
    {
      title: 'New Project',
      description: 'Add another website or campaign',
      icon: Plus,
      color: 'from-pink-600 to-pink-700',
      href: '/projects/new',
      disabled: false
    },
    {
      title: 'Marketing Strategy',
      description: 'Coming Soon',
      icon: Target,
      color: 'from-orange-600 to-orange-700',
      href: '/agents/marketing',
      disabled: true,
      comingSoon: true
    },
    {
      title: 'AI Insights',
      description: 'Coming Soon',
      icon: Sparkles,
      color: 'from-indigo-600 to-indigo-700',
      href: '/agents',
      disabled: true,
      comingSoon: true
    }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {quickActions.map((action, index) => (
        <Button
          key={index}
          variant="outline"
          className={`h-auto p-4 flex flex-col items-center text-center transition-all duration-200 ${
            action.disabled 
              ? 'opacity-60 cursor-not-allowed bg-gray-50 dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-none' 
              : 'hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
          onClick={() => !action.disabled && navigate(action.href)}
          disabled={action.disabled}
        >
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${action.color} flex items-center justify-center mb-3 ${
            action.disabled ? 'opacity-40 grayscale' : ''
          }`}>
            <action.icon className="w-5 h-5 text-white" />
          </div>
          <div className="relative">
            <div className="flex items-center justify-center gap-2 mb-1">
              <p className={`font-medium text-sm ${action.disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>
                {action.title}
              </p>
              {action.beta && (
                <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full font-medium">
                  Beta
                </span>
              )}
            </div>
            <p className={`text-xs ${
              action.comingSoon 
                ? 'text-orange-600 dark:text-orange-400 font-medium bg-orange-100 dark:bg-orange-900/30 px-2 py-1 rounded-full inline-block' 
                : action.disabled 
                  ? 'text-gray-400 dark:text-gray-500' 
                  : 'text-gray-500 dark:text-gray-400'
            }`}>
              {action.description}
            </p>
          </div>
        </Button>
      ))}
    </div>
  );
}